// An agent share's chat (RFC-043 model 20): a caller, the owner or a coach
// asks the shared agent, over the share's socket as an agent.request; the
// agent's working frames and its answer come back as agent.stream and
// agent.result, to the one who asked. The transcript everyone sees is the
// timeline's, so the box shows only what is in flight — the question and
// the agent's working text — and a refusal, which the timeline never has.
import { useState, type FormEvent } from "react";
import type { AgentInfo } from "../protocol";
import { t, word } from "../i18n";

/** One exchange in the thread. */
export interface AgentExchange {
  id: string;
  skill: string;
  text: string;
  /** The agent's working text so far, from its stream frames. */
  working?: string;
  answer?: string;
  error?: string;
  ts: string;
}

/** The text an A2A reply carries: a task's status message, else its artifacts', else a message's. */
export function replyText(result: unknown): string {
  const r = result as { status?: { message?: { parts?: { kind?: string; text?: string }[] } }; artifacts?: { parts?: { kind?: string; text?: string }[] }[]; parts?: { kind?: string; text?: string }[] } | null;
  if (!r || typeof r !== "object") return "";
  const parts = r.status?.message?.parts ?? r.parts ?? r.artifacts?.flatMap((a) => a.parts ?? []) ?? [];
  return parts
    .filter((p) => p.kind === "text")
    .map((p) => p.text ?? "")
    .join("");
}

/** A task ended otherwise than completed, said in words: each state the words name, any other as stopped (byj-1wv.115.49). */
const endedWords = (state: string | undefined) => word("session:agentEnded", state ?? "") ?? t("session:agentEnded.unknown");

/** What an agent.stream or agent.result envelope says for the thread. */
export function readFrame(data: unknown): { working?: string; answer?: string; error?: string; final: boolean } {
  const env = data as { result?: unknown; error?: { message?: string; data?: { detail?: string } } } | null;
  if (!env || typeof env !== "object") return { error: t("session:agentUnreadable"), final: true };
  if (env.error) return { error: env.error.data?.detail ?? env.error.message ?? t("session:agentUnreadable"), final: true };
  const res = env.result as { kind?: string; final?: boolean; status?: { state?: string } } | undefined;
  const text = replyText(env.result);
  if (res?.kind === "status-update") {
    const state = res.status?.state;
    if (res.final || (state && state !== "working" && state !== "submitted")) {
      return state === "completed" ? { answer: text, final: true } : { error: endedWords(state), final: true };
    }
    return { working: text, final: false };
  }
  if (res?.kind === "task") {
    const state = res.status?.state;
    if (state === "submitted" || state === "working") return { working: text, final: false };
    return state === "completed" ? { answer: text, final: true } : { error: endedWords(state), final: true };
  }
  return { answer: text, final: true };
}

export function AgentChat({
  agent,
  thread,
  disabled,
  onAsk,
}: {
  agent: AgentInfo;
  thread: AgentExchange[];
  disabled: boolean;
  onAsk: (skill: string, text: string) => boolean;
}) {
  const [skill, setSkill] = useState(agent.skills[0]?.id ?? "");
  const [text, setText] = useState("");
  const last = thread[thread.length - 1];
  const waiting = last !== undefined && last.answer === undefined && last.error === undefined;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const q = text.trim();
    if (!q || !skill) return;
    if (onAsk(skill, q)) setText("");
  };
  return (
    <section className="agent-chat" data-testid="agent-chat" aria-label={t("session:agentChat")}>
      <h3>
        {t("session:agentChat")} · {agent.name}
      </h3>
      {agent.description && <p className="muted small">{agent.description}</p>}
      <ol className="agent-thread">
        {thread.map((x) => (
          <li key={x.id} data-testid="agent-exchange">
            <p className="agent-q">
              <span className="muted small">{x.skill}</span> {x.text}
            </p>
            {x.answer !== undefined ? (
              <p className="agent-a">{x.answer}</p>
            ) : x.error !== undefined ? (
              <p className="agent-a error">{x.error}</p>
            ) : (
              <p className="agent-a muted">{x.working ? x.working : t("session:agentWorking")}</p>
            )}
          </li>
        ))}
      </ol>
      <form onSubmit={submit} className="row wrap">
        {agent.skills.length > 1 && (
          <select value={skill} onChange={(e) => setSkill(e.target.value)} aria-label={t("session:agentSkill")} disabled={disabled || waiting}>
            {agent.skills.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name ?? s.id}
              </option>
            ))}
          </select>
        )}
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={t("session:agentPlaceholder")}
          disabled={disabled || waiting}
          aria-label={t("session:agentPlaceholder")}
        />
        <button type="submit" disabled={disabled || waiting || !text.trim()}>
          {t("session:agentAsk")}
        </button>
      </form>
    </section>
  );
}
